import React, { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAdminActivitySummary } from '../../services/admin/activityService';
import { classifyAdminCallableError } from './adminUserDisplay';
import {
  ACTIVITY_KPI_DEFS,
  formatActivityClock,
  formatActivityNumber,
  normalizeActivityDaily,
  readActivityPath,
} from './adminActivityDisplay';
import AdminActivityChart from './AdminActivityChart';

function errorMessageFor(kind, t) {
  if (kind === 'failed-precondition') return t('admin.activity.error.index');
  if (kind === 'permission-denied') return t('admin.activity.error.permission');
  if (kind === 'unavailable') return t('admin.activity.error.unavailable');
  return t('admin.activity.error');
}

function ActivityKpiSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-hidden="true">
      {ACTIVITY_KPI_DEFS.map((card) => (
        <div key={card.id} className="h-28 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
      ))}
    </div>
  );
}

export default function AdminActivityPage() {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [errorKind, setErrorKind] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async ({ keep = false } = {}) => {
    if (keep) {
      setRefreshing(true);
    } else {
      setStatus('loading');
      setSummary(null);
      setErrorKind(null);
    }
    try {
      const data = await getAdminActivitySummary();
      setSummary(data);
      setErrorKind(null);
      setStatus('ready');
    } catch (err) {
      setSummary(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const daily = normalizeActivityDaily(summary?.propertyCreation?.daily);
  const updatedLabel = t('admin.activity.updatedAt').replace(
    '{time}',
    formatActivityClock(summary?.generatedAt, language),
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Activity className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{t('admin.activity.title')}</h1>
            <p className="mt-1 text-sm text-slate-600">{t('admin.activity.subtitle')}</p>
            {status === 'ready' ? (
              <p className="mt-1 text-xs text-slate-500">{updatedLabel}</p>
            ) : null}
            {refreshing ? (
              <p className="mt-1 text-xs text-indigo-600" aria-live="polite">
                {t('admin.activity.refreshing')}
              </p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => load({ keep: status === 'ready' })}
          disabled={status === 'loading' || refreshing}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          {t('admin.activity.refresh')}
        </button>
      </div>

      {status === 'loading' ? (
        <div aria-live="polite">
          <p className="sr-only">{t('admin.activity.loading')}</p>
          <ActivityKpiSkeleton />
          <div className="mt-6 h-64 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessageFor(errorKind, t)}</p>
          <button
            type="button"
            onClick={() => load()}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.activity.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && summary ? (
        <>
          <section aria-label={t('admin.activity.kpisTitle')}>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {ACTIVITY_KPI_DEFS.map((card) => {
                const value = readActivityPath(summary, card.valuePath);
                return (
                  <article
                    key={card.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                    title={card.hintKey ? t(card.hintKey) : undefined}
                  >
                    <p className="text-sm font-medium text-slate-600">{t(card.titleKey)}</p>
                    <p className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">
                      {formatActivityNumber(value)}
                    </p>
                    {card.hintKey ? (
                      <p className="mt-2 text-xs leading-snug text-slate-500">{t(card.hintKey)}</p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>
          <AdminActivityChart daily={daily} language={language} t={t} />
        </>
      ) : null}
    </div>
  );
}
